import { useEffect, useMemo, type ReactNode } from 'react';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { MoveCard } from '@/components/molecules/MoveCard';
import { useExpandedState } from '@/hooks/useExpandedState';
import { dwrClassesById } from '@/data/classes';
import { findMoveByIdGlobal } from '@/data/moves';
import type { Character, MoveRef } from '@/schemas/character';
import type { ClassId } from '@/schemas/classId';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { MoveSetupPanel } from '../../sections/MoveSetupPanel';
import { updateMoveChoiceGroup, updateMoveNarrativeField } from '../../services/moveRefMutations';

/**
 * **FI-84 enhanced (2026-05-24)** — UI cascade ricorsiva delle mosse
 * concesse via `grantsMoveChoice` sulla mossa-target multiclasse.
 *
 * Modello SSOT: `grantedChain` è una lista ordinata di step. La UI
 * renderizza N `CascadeStepRow` walking attraverso i livelli — ogni
 * step si attiva quando l'anchor del livello precedente ha una grant
 * compatibile (`from-list` esplicita, `from-choice-mapping` auto-derive).
 *
 * Profondità arbitraria: l'algoritmo continua finché c'è una grant
 * attiva sulla mossa corrente, garantendo coverage di cascade N-livelli
 * (caso pilota 3-livelli: Faro → Ordine Sovr → Vincolo Lama).
 */
export interface GrantedOfTargetSectionProps {
  character: Character;
  selectedTargetMove: Move | undefined;
  /** Per `from-choice-mapping` sulla target: choice del setup target che
   *  attiva il mapping (es. `selectedChoices[0]`). */
  targetSelectedChoiceId: string | undefined;
  grantedChain: readonly {
    selectedMoveId: string;
    selectedSetup?: Omit<MoveRef, 'id'>;
  }[];
  onGrantedChainChange: (
    nextChain: readonly {
      selectedMoveId: string;
      selectedSetup?: Omit<MoveRef, 'id'>;
    }[],
  ) => void;
}

export function GrantedOfTargetSection({
  character,
  selectedTargetMove,
  targetSelectedChoiceId,
  grantedChain,
  onGrantedChainChange,
}: GrantedOfTargetSectionProps) {
  if (selectedTargetMove === undefined) return null;
  if (selectedTargetMove.grantsMoveChoice === undefined) return null;
  return (
    <CascadeWalker
      character={character}
      anchorMove={selectedTargetMove}
      anchorSelectedChoiceId={targetSelectedChoiceId}
      anchorIndex={0}
      grantedChain={grantedChain}
      onGrantedChainChange={onGrantedChainChange}
    />
  );
}

interface CascadeWalkerProps {
  character: Character;
  anchorMove: Move;
  /** Choice della MOSSA ANCHOR (settata nel suo setup) che attiva una
   *  grant from-choice-mapping. Per from-list non utilizzata. */
  anchorSelectedChoiceId: string | undefined;
  anchorIndex: number;
  grantedChain: GrantedOfTargetSectionProps['grantedChain'];
  onGrantedChainChange: GrantedOfTargetSectionProps['onGrantedChainChange'];
}

/**
 * Walker che renderizza ricorsivamente i livelli della cascade.
 * `anchorIndex` indica quale step in `grantedChain` rappresenta il
 * granted di `anchorMove`.
 *
 * **Auto-derive `from-choice-mapping`** (fix cascade level > 0): se la
 * grant dell'anchor è `from-choice-mapping` e l'utente ha già compilato
 * la `anchorSelectedChoiceId`, il walker sincronizza automaticamente
 * `grantedChain[anchorIndex]` con la mossa derivata dal mapping. Senza
 * questo trigger, il sub-walker resterebbe in attesa di uno step
 * utente che non arriverà mai (la mossa è auto-derivata, non scelta).
 */
function CascadeWalker({
  character,
  anchorMove,
  anchorSelectedChoiceId,
  anchorIndex,
  grantedChain,
  onGrantedChainChange,
}: CascadeWalkerProps) {
  const grant = anchorMove.grantsMoveChoice;
  // Auto-derive `from-choice-mapping`: deve essere chiamato prima di
  // qualunque early-return per rispettare le rules of hooks.
  useAutoDeriveFromMapping({
    grant,
    anchorSelectedChoiceId,
    anchorIndex,
    grantedChain,
    onGrantedChainChange,
  });
  if (grant === undefined) return null;
  if (grant.kind !== 'from-list' && grant.kind !== 'from-choice-mapping') return null;
  const step = grantedChain[anchorIndex];
  return (
    <CascadeStepRow
      character={character}
      anchorMove={anchorMove}
      anchorIndex={anchorIndex}
      grantedChain={grantedChain}
      step={step}
      onGrantedChainChange={onGrantedChainChange}
    />
  );
}

/**
 * **Fix cascade level > 0 (2026-05-24)** — Per `from-choice-mapping`, la
 * mossa concessa non è scelta dall'utente: deriva dalla choice
 * compilata nell'anchor (es. `paladino.ordine-sovrannaturale` con
 * `selectedChoices = ['vincolo-della-lama']` → granted =
 * `warlock.vincolo-della-lama`). Questo hook sincronizza
 * `grantedChain[anchorIndex]` con la mossa derivata, troncando gli step
 * successivi quando la choice dell'anchor cambia (il sub-cascade può
 * essere invalido).
 */
function useAutoDeriveFromMapping(props: {
  grant: Move['grantsMoveChoice'];
  anchorSelectedChoiceId: string | undefined;
  anchorIndex: number;
  grantedChain: GrantedOfTargetSectionProps['grantedChain'];
  onGrantedChainChange: GrantedOfTargetSectionProps['onGrantedChainChange'];
}): void {
  const { grant, anchorSelectedChoiceId, anchorIndex, grantedChain, onGrantedChainChange } = props;
  const mappedId =
    grant?.kind === 'from-choice-mapping' && anchorSelectedChoiceId !== undefined
      ? grant.mapping[anchorSelectedChoiceId]
      : undefined;
  const currentStepId = grantedChain[anchorIndex]?.selectedMoveId;
  useEffect(() => {
    if (mappedId === undefined) return;
    if (currentStepId === mappedId) return;
    // Tronca chain[anchorIndex+] e auto-set lo step con la mossa derivata.
    const truncated = grantedChain.slice(0, anchorIndex);
    onGrantedChainChange([...truncated, { selectedMoveId: mappedId }]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mappedId, currentStepId, anchorIndex]);
}

interface CascadeStepRowProps {
  character: Character;
  anchorMove: Move;
  anchorIndex: number;
  grantedChain: GrantedOfTargetSectionProps['grantedChain'];
  step: { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> } | undefined;
  onGrantedChainChange: GrantedOfTargetSectionProps['onGrantedChainChange'];
}

function CascadeStepRow({
  character,
  anchorMove,
  anchorIndex,
  grantedChain,
  step,
  onGrantedChainChange,
}: CascadeStepRowProps) {
  const selectedMove = step?.selectedMoveId ? findMoveByIdGlobal(step.selectedMoveId) : undefined;
  const handlers = useCascadeStepHandlers(grantedChain, anchorIndex, step, onGrantedChainChange);
  return (
    <div className="border-rule bg-paper-soft/40 border p-3">
      <p className="font-display text-ink-soft mb-2 text-[0.78rem] tracking-[0.08em] uppercase">
        Concessa da «{anchorMove.name}»
      </p>
      <CascadeStepBody
        character={character}
        anchorMove={anchorMove}
        anchorIndex={anchorIndex}
        selectedMove={selectedMove}
        step={step}
        grantedChain={grantedChain}
        onSelectMoveId={handlers.onSelectMoveId}
        onSetupChange={handlers.onSetupChange}
        onGrantedChainChange={onGrantedChainChange}
      />
    </div>
  );
}

interface CascadeStepBodyProps {
  character: Character;
  anchorMove: Move;
  anchorIndex: number;
  selectedMove: Move | undefined;
  step: { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> } | undefined;
  grantedChain: GrantedOfTargetSectionProps['grantedChain'];
  onSelectMoveId: (id: string) => void;
  onSetupChange: (next: Omit<MoveRef, 'id'>) => void;
  onGrantedChainChange: GrantedOfTargetSectionProps['onGrantedChainChange'];
}

function CascadeStepBody({
  character,
  anchorMove,
  anchorIndex,
  selectedMove,
  step,
  grantedChain,
  onSelectMoveId,
  onSetupChange,
  onGrantedChainChange,
}: CascadeStepBodyProps) {
  const grant = anchorMove.grantsMoveChoice;
  const isFromList = grant?.kind === 'from-list';
  return (
    <>
      {renderFromListPool(grant, step?.selectedMoveId, onSelectMoveId)}
      {renderSelectedMoveDetails({
        character,
        selectedMove,
        step,
        isFromList,
        onSetupChange,
      })}
      {selectedMove && (
        <div className="mt-3">
          <CascadeWalker
            character={character}
            anchorMove={selectedMove}
            anchorSelectedChoiceId={step?.selectedSetup?.selectedChoices?.[0]}
            anchorIndex={anchorIndex + 1}
            grantedChain={grantedChain}
            onGrantedChainChange={onGrantedChainChange}
          />
        </div>
      )}
    </>
  );
}

function renderFromListPool(
  grant: Move['grantsMoveChoice'],
  selectedMoveId: string | undefined,
  onSelect: (id: string) => void,
): ReactNode {
  if (grant?.kind !== 'from-list') return null;
  return (
    <FromListPool
      moveOptions={grant.moveOptions}
      selectedMoveId={selectedMoveId}
      onSelect={onSelect}
    />
  );
}

function renderSelectedMoveDetails(props: {
  character: Character;
  selectedMove: Move | undefined;
  step: { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> } | undefined;
  isFromList: boolean;
  onSetupChange: (next: Omit<MoveRef, 'id'>) => void;
}): ReactNode {
  const { character, selectedMove, step, isFromList, onSetupChange } = props;
  if (!selectedMove) return null;
  if (hasMoveSetup(selectedMove)) {
    return (
      <NestedSetupPanel
        character={character}
        move={selectedMove}
        setup={step?.selectedSetup}
        onChange={onSetupChange}
      />
    );
  }
  // **FI-86 (2026-05-25)**: per le mosse description-only auto-derivate
  // (es. Vincolo della Catena dopo Ordine Sovrannaturale), mostriamo la
  // MoveCard espansa con la description completa invece di una semplice
  // riga `{nome} ({classe})`. Pattern coerente con il pool from-list
  // (entrambi MoveCard expanded) e con `<CrossClassGrantedSection>` del
  // builder L1.
  if (!isFromList) return <AutoDerivedMoveCard move={selectedMove} />;
  return null;
}

function useCascadeStepHandlers(
  grantedChain: GrantedOfTargetSectionProps['grantedChain'],
  anchorIndex: number,
  step: { selectedMoveId: string; selectedSetup?: Omit<MoveRef, 'id'> } | undefined,
  onGrantedChainChange: GrantedOfTargetSectionProps['onGrantedChainChange'],
): {
  onSelectMoveId: (nextId: string) => void;
  onSetupChange: (nextSetup: Omit<MoveRef, 'id'>) => void;
} {
  const onSelectMoveId = (nextId: string): void => {
    // Tronca tutti gli step successivi (sub-cascade può cambiare).
    const truncated = grantedChain.slice(0, anchorIndex);
    onGrantedChainChange([...truncated, { selectedMoveId: nextId }]);
  };
  const onSetupChange = (nextSetup: Omit<MoveRef, 'id'>): void => {
    if (step === undefined) return;
    const next = [...grantedChain];
    next[anchorIndex] = { selectedMoveId: step.selectedMoveId, selectedSetup: nextSetup };
    onGrantedChainChange(next);
  };
  return { onSelectMoveId, onSetupChange };
}

/**
 * **FI-86 (2026-05-25)** — Render della mossa cross-class auto-derivata
 * description-only come `<MoveCard>` espansa. Stesso pattern visivo del
 * `<FromListPool>` (mosse selezionabili) e di `<GrantedMoveCard>` nel
 * builder L1: badge classe + description completa. `expanded={true}` per
 * default (no toggle): la card è informativa, non interattiva.
 */
function AutoDerivedMoveCard({ move }: { move: Move }) {
  const className = getClassName(move);
  const trailing = className ? (
    <span className="bg-ink-soft text-paper-bright font-display inline-block px-[0.45em] py-[0.1em] text-[0.6rem] tracking-[0.1em] uppercase">
      {className}
    </span>
  ) : undefined;
  return (
    <div className="mt-3">
      <MoveCard
        move={move}
        fallbackId={move.id}
        expanded={true}
        onToggle={noop}
        {...(trailing ? { trailing } : {})}
      />
    </div>
  );
}

const noop = (): void => {
  /* MoveCard auto-derivata è expanded fissa, no toggle */
};

function FromListPool({
  moveOptions,
  selectedMoveId,
  onSelect,
}: {
  moveOptions: readonly string[];
  selectedMoveId: string | undefined;
  onSelect: (id: string) => void;
}) {
  const moveIds = useMemo(() => [...moveOptions], [moveOptions]);
  const expandState = useExpandedState(moveIds);
  return (
    <RadioGroup value={selectedMoveId ?? ''} className="gap-2">
      {moveOptions.map((id) => {
        const move = findMoveByIdGlobal(id);
        const inputId = `granted-cascade-${id}`;
        return (
          <MoveCard
            key={id}
            move={move}
            fallbackId={id}
            expanded={expandState.expanded.has(id)}
            onToggle={() => expandState.toggleExpanded(id)}
            labelHtmlFor={inputId}
            selected={selectedMoveId === id}
            leading={
              <RadioGroupItem
                value={id}
                id={inputId}
                className="mt-1"
                onClick={() => onSelect(id)}
              />
            }
          />
        );
      })}
    </RadioGroup>
  );
}

function NestedSetupPanel({
  character,
  move,
  setup,
  onChange,
}: {
  character: Character;
  move: Move;
  setup: Omit<MoveRef, 'id'> | undefined;
  onChange: (next: Omit<MoveRef, 'id'>) => void;
}) {
  const ownedMoveIds = useMemo(() => character.moves.map((m) => m.id), [character.moves]);
  const moveRef = buildMoveRefFromSetup(move.id, setup);
  return (
    <div className="border-rule bg-paper-bright mt-3 border p-3">
      <p className="font-display text-ink-soft mb-2 text-[0.78rem] tracking-[0.08em] uppercase">
        Setup di «{move.name}»
      </p>
      <MoveSetupPanel
        move={move}
        selectedByGroup={buildSelectedByGroup(move, moveRef)}
        narrativeValues={moveRef.narrativeFieldValues ?? {}}
        recruitOutcome={moveRef.recruitOutcome}
        archetypeId={character.archetypeId}
        ownedMoveIds={ownedMoveIds}
        onChoiceGroupChange={(moveId, groupId, selectedIds) => {
          const updated = updateMoveChoiceGroup(moveRef, moveId, groupId, selectedIds);
          onChange(extractSetup(updated));
        }}
        onNarrativeFieldChange={(moveId, fieldId, value) => {
          const updated = updateMoveNarrativeField(moveRef, moveId, fieldId, value);
          onChange(extractSetup(updated));
        }}
      />
    </div>
  );
}

/**
 * Risolve l'anchor iniziale della cascade.
 *  - `from-list`: l'anchor è la target stessa; `startIndex` = 0.
 *  - `from-choice-mapping`: l'anchor è la target; `useAutoDeriveFromMapping`
 *    auto-popola `chain[0]` con la mossa derivata.
 *
 * **NOTA**: `resolveInitialAnchor` rimossa nel fix cascade level > 0:
 * il pattern è ora uniforme — l'anchor del livello 0 è SEMPRE la target,
 * e il walker gestisce auto-derive via `useAutoDeriveFromMapping` per
 * tutti i livelli (target incluso).
 */

function buildMoveRefFromSetup(id: string, setup: Omit<MoveRef, 'id'> | undefined): MoveRef {
  return {
    id,
    selectedChoices: setup?.selectedChoices ?? [],
    ...(setup?.selectedChoiceGroups && { selectedChoiceGroups: setup.selectedChoiceGroups }),
    ...(setup?.narrativeFieldValues && { narrativeFieldValues: setup.narrativeFieldValues }),
  };
}

function getClassName(move: Move): string {
  const classId = move.classId as ClassId | undefined;
  return classId ? (dwrClassesById[classId]?.name ?? '') : '';
}

function buildSelectedByGroup(move: Move, moveRef: MoveRef): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  if (move.choices) result.main = [...moveRef.selectedChoices];
  if (moveRef.selectedChoiceGroups) {
    for (const [groupId, ids] of Object.entries(moveRef.selectedChoiceGroups)) {
      result[groupId] = [...ids];
    }
  }
  return result;
}

function extractSetup(moveRef: MoveRef): Omit<MoveRef, 'id'> {
  const setup: Omit<MoveRef, 'id'> = { selectedChoices: moveRef.selectedChoices };
  if (moveRef.selectedChoiceGroups) setup.selectedChoiceGroups = moveRef.selectedChoiceGroups;
  if (moveRef.narrativeFieldValues) setup.narrativeFieldValues = moveRef.narrativeFieldValues;
  return setup;
}
